import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { arrayMove, rectSortingStrategy, SortableContext, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { Box } from '@mui/material';

import { CatalogDTO } from '@/api/generated';

import SortableCatalogItemCard from './SortableCatalogItemCard';

interface SortableCatalogItemListProps {
  value: CatalogDTO[];
  onChange: (newValue: CatalogDTO[]) => void;
  processId: string;
  onDelete: () => void;
}

const SortableCatalogItemList: FC<SortableCatalogItemListProps> = ({ processId, value, onChange, onDelete }) => {
  const [catalogItems, setCatalogItems] = useState(value);

  const catalogIds = useMemo(() => catalogItems.map(({ catalogId }) => catalogId!), [catalogItems]);

  useEffect(
    function syncValue() {
      if (catalogItems === value) return;

      setCatalogItems(value);
    },
    [value] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      const originalTestsList = catalogItems;

      if (active.id !== over?.id) {
        const oldIndex = originalTestsList.findIndex(({ catalogId }) => catalogId === active?.id);
        const newIndex = originalTestsList.findIndex(({ catalogId }) => catalogId === over?.id);
        const updatedTestsList = arrayMove(originalTestsList, oldIndex, newIndex);
        onChange(updatedTestsList);
        setCatalogItems(updatedTestsList);
      }
    },
    [catalogItems, onChange]
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToParentElement]}
    >
      <SortableContext items={catalogIds} strategy={rectSortingStrategy}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 1,
            maxWidth: 685
          }}
        >
          {catalogItems?.map((item, index) => (
            <SortableCatalogItemCard
              key={item.catalogId}
              processId={processId}
              id={item.catalogId!}
              index={index}
              name={item.name!}
              onDelete={onDelete}
            />
          ))}
        </Box>
      </SortableContext>
    </DndContext>
  );
};

export default SortableCatalogItemList;
